import { useCallback } from 'react'

import { Call, LocationOn } from '@mui/icons-material'
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'

import {
  computeVoiceEventId,
  fitMap,
  setVoiceEventMarker,
  useVisibleVoiceEventMarkerId,
} from '@/map'
import { geo, gis } from '@/server-data'

import { useTexts } from '../../useTexts'
import { useDriverName } from './hooks/useDriverName'

import { preview } from '@/components/form/preview'
import { Duration } from '../Duration'
import { SentBy } from '../SentBy'
import { Status } from '../Status'

interface Props {
  item: uui.domain.client.rm.OrderStepVoiceCall
}

export function VoiceCallNotificationItem(props: Props) {
  const { item } = props
  const { startTs, driverId, driverPhoneNumber, orderStepPhoneNumber } = item

  const texts = useTexts()

  const visibleMarkerId = useVisibleVoiceEventMarkerId()
  const driverName = useDriverName(driverId)

  const markerId = computeVoiceEventId(item)
  const visible = markerId === visibleMarkerId

  const invalidLocation =
    !item.startLatLng || (item.startLatLng[0] === 0 && item.startLatLng[1] === 0)

  const showVoiceCallMarker = useCallback(() => {
    if (visible) {
      setVoiceEventMarker({
        event: null,
      })

      return
    }

    const latLng = geo.latLngFromJSON(item.startLatLng)
    const lonLat = gis.fromLatLngToCoordinate(latLng)

    setVoiceEventMarker({
      event: {
        id: markerId,
        latLng,
        lonLat,
        visible: true,
      },
    })

    fitMap([latLng], { minResolution: 3, preventIfVisible: true }, 'clear')
  }, [item, visible, markerId])

  return (
    <preview.Section>
      <Paper elevation={0} variant="outlined" sx={{ padding: 2 }}>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1">
              <Box display="flex" alignItems="center">
                <Call sx={{ mr: 1 }} />
                {texts.voiceCall}
              </Box>
            </Typography>

            <Status sentTs={startTs} />
          </Stack>

          <Stack direction="column" spacing={1} alignItems="flex-start">
            <Duration durationSec={item.durationSec} />

            <Tooltip
              placement="top"
              title={texts.noGpsDataAvailable}
              disableHoverListener={!invalidLocation}
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={invalidLocation}
                  onClick={showVoiceCallMarker}
                  startIcon={<LocationOn />}
                  data-testid="communication-tab-show-voice-call-on-map"
                  data-trackid="communication-tab-show-voice-call-on-map"
                >
                  {visible ? texts.hide : texts.show}
                </Button>
              </span>
            </Tooltip>

            <SentBy
              recipient={orderStepPhoneNumber}
              sender={texts.byDriver(driverName, driverPhoneNumber)}
            />
          </Stack>
        </Stack>
      </Paper>
    </preview.Section>
  )
}
